import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, shareReplay, switchMap } from 'rxjs/operators';
import { SocketIoService } from '@ep-om/core/services/socket-io.service';
import { RealtimeEngineService } from './realtime-engine.service';
import { RealtimeV2SocketService } from './realtime-v2-socket.service';

/**
 * Stato di connessione realtime svincolato dal motore. Chi deve solo sapere "il socket e' su?" inietta
 * questo invece del singolo socket service: l'arbitro (engine$) sceglie la sorgente corretta (v1/v2),
 * reattivo anche allo switch in-place (setEngine, senza reload).
 *
 * NON esporre qui logica specifica di un motore (es. le command/option aggiuntive del v2): chi ne ha
 * bisogno continua a iniettare il socket service dedicato.
 */
@Injectable({ providedIn: 'root' })
export class RealtimeConnectionService {
  /** True quando il socket del motore attivo e' connesso. */
  readonly connected$: Observable<boolean> = this.engine.engine$.pipe(
    switchMap((engine) => (engine === 'v2' ? this.v2.connected$ : this.v1.connected$)),
    distinctUntilChanged(),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  /**
   * Emette ad ogni (ri)connessione del motore attivo (stessa semantica di `SocketIoService.uponConnection$`).
   * Aggancio per le entita' "calcolate" sincronizzate in pull via HTTP + cursore lastUpdate.
   */
  readonly uponConnection$: Observable<boolean> = this.connected$.pipe(
    debounceTime(300),
    filter((connected) => connected === true)
  );

  /** Snapshot sincrono, rimpiazza i vari `socket.connected$.value`. */
  get connected(): boolean {
    return this.engine.isV2() ? this.v2.connected$.value : this.v1.connected$.value;
  }

  constructor(
    private engine: RealtimeEngineService,
    private v1: SocketIoService,
    private v2: RealtimeV2SocketService
  ) {}
}
